/** @jsxImportSource @emotion/react */

import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { trackEvent } from 'shared/analytics';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import { AppDispatch } from 'store/store';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { selectProcess } from 'modules/players/store';
import { upsertTournamentUser } from 'modules/players/store/actions';
import PlayerForm, { SubmitPlayerForm } from '../components/player-form';

function PlayerCreatePage() {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { info } = useOrganizer();
  const { isProcessing } = useSelector(selectProcess);
  const [isLoading, setIsLoading] = useState(false);
  const { setHeaderContent } = useMainLayout();

  const breadcrumb = useMemo(
    () => (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: t`Игроки`, to: Pages.PLAYERS.INDEX },
          { label: t`Добавление игрока`, to: Pages.PLAYERS.CREATE },
        ]}
      />
    ),
    [t],
  );

  const onSubmit = useCallback<SubmitPlayerForm>(
    ({ values, formikHelpers }) => {
      if (!info) {
        return;
      }

      setIsLoading(true);

      const height = values.height && !isNaN(Number(values.height)) ? Number(values.height) : undefined;
      const weight = values.weight && !isNaN(Number(values.weight)) ? Number(values.weight) : undefined;

      dispatch(
        upsertTournamentUser({
          tournamentId: info.id,
          name: values.name,
          surname: values.surname,
          middleName: values.middleName,
          birthDate: values.birthDate,
          photo: values.photo && values.photo[0] instanceof File ? (values.photo as File[]) : undefined,
          height,
          weight,
        }),
      )
        .unwrap()
        .then((data) => {
          StepperMainTool.setByPath('stepper:main:createPlayer', true);
          trackEvent('Player Created', { source: 'players-page' });
          navigate(Pages.PLAYERS.builders.view(data.id));
          toast.success(t`Игрок успешно создан`);
        })
        .catch((error) => {
          toastApiError(error, msg`Не удалось создать игрока`);
        })
        .finally(() => {
          formikHelpers.setSubmitting(false);
          setIsLoading(false);
        });
    },
    [info, dispatch, navigate, t, toastApiError],
  );

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <div className={'w-full px-6 pt-6'}>
        <PlayerForm onSubmit={onSubmit} isLoading={isProcessing || isLoading} />
      </div>
    </>
  );
}

export default PlayerCreatePage;
